import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Calendar, Briefcase, Clock, UserCog, LayoutDashboard, BookOpen, Lock, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import subscriptionStore from '../../store/subscription-store';
import UpgradeRequiredModal from '../ui/upgrade-required-modal';

const MobileNavigation: React.FC = observer(() => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [lockedFeature, setLockedFeature] = useState<{ name: string; plan: 'Premium' | 'Premium+' }>({ name: '', plan: 'Premium' });

  // Vérifier les features disponibles
  const hasAdvancedAnalytics = subscriptionStore.currentSubscription?.advancedAnalytics ?? false;
  const hasReviewSystem = subscriptionStore.currentSubscription?.reviewSystem ?? false;

  // Navigation items with icons
  const navItems = [
    {
      path: `/${currentLanguage}/provider/dashboard`,
      label: t('sidebar.dashboard', 'Tableau de bord'),
      icon: <LayoutDashboard size={20} />
    },
    {
      path: `/${currentLanguage}/provider/calendar`,
      label: t('sidebar.calendar', 'Calendrier'),
      icon: <Calendar size={20} />
    },
    {
      path: `/${currentLanguage}/provider/reservations`,
      label: t('sidebar.reservations', 'Réservations'),
      icon: <BookOpen size={20} />,
      locked: !hasAdvancedAnalytics
    },
    {
      path: `/${currentLanguage}/provider/services`,
      label: t('sidebar.services', 'Prestations'),
      icon: <Briefcase size={20} />
    },
    {
      path: `/${currentLanguage}/provider/availability`,
      label: t('sidebar.availability', 'Disponibilités'),
      icon: <Clock size={20} />
    },
    {
      path: `/${currentLanguage}/provider/reviews`,
      label: t('sidebar.reviews', 'Avis'),
      icon: <Star size={20} />,
      locked: !hasReviewSystem
    },
    {
      path: `/${currentLanguage}/provider/profile`,
      label: t('sidebar.profile', 'Profil'),
      icon: <UserCog size={20} />
    }
  ];

  return (
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-800 shadow-lg border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-around items-center h-16">
        {navItems.map((item: any) => {
          // Vérifier si le chemin actuel correspond à la route (sans vérifier exactement)
          const isActive = location.pathname.includes(item.path.split('/').slice(-1)[0]);

          if (item.locked) {
            return (
              <div
                key={item.path}
                className="flex flex-col items-center justify-center py-2 px-3 opacity-50 cursor-pointer hover:opacity-70 transition-opacity text-gray-400 dark:text-gray-500 relative"
                title="Fonctionnalité Premium"
                onClick={() => {
                  setLockedFeature({ name: item.label, plan: 'Premium' });
                  setIsUpgradeModalOpen(true);
                }}
              >
                <div className="p-1.5 rounded-full relative">
                  {item.icon}
                  <Lock size={10} className="absolute -top-1 -right-1 text-amber-500" />
                </div>
                <span className="text-xs mt-1">{item.label}</span>
              </div>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-2 px-3 transition-all duration-200 ${
                  isActive
                    ? 'text-primary dark:text-primary-light'
                    : 'text-gray-500 dark:text-gray-400'
                }`
              }
            >
              <div className={`p-1.5 rounded-full ${
                isActive
                  ? 'bg-primary/10 dark:bg-primary-dark/20'
                  : ''
              }`}>
                {item.icon}
              </div>
              <span className="text-xs mt-1">{item.label}</span>
            </NavLink>
          );
        })}
        </div>

        {/* Upgrade Required Modal */}
        <UpgradeRequiredModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          featureName={lockedFeature.name}
          requiredPlan={lockedFeature.plan}
        />
      </nav>
  );
});

export default MobileNavigation;
